/**
 * QualidadeIaPage — Brief 4 (Gestor) · Tela Qualidade IA (observabilidade de IA).
 *
 * Rota: /supervisor/qualidade-ia. Dashboard read-only de custo/performance dos
 * agentes de IA, reusando `GET /v1/quality/metrics/agents` (hook `useQualityAgents`
 * + adapter `adaptAgents` da Tela 6 do Supervisor — sem duplicar agregação). Janela
 * macro de 30 dias (snapshot estratégico, sem filtro de período no gabarito).
 *
 * Padrão D-B (defense-in-depth): gate próprio (`quality-aggregations-read`) — o
 * inner com o hook só monta quando a cap está presente, então a query NÃO dispara
 * sem a capability (gatear o fetch, não só o render).
 *
 * Seção "Custo por conversa": gateada em `ai-usage-read` — `enabled=false` sem
 * a cap, o GET nunca dispara. `topConversations=null` → view omite a seção.
 */
import { CapabilityLockedBanner } from '@lume/ui-components';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { QualidadeIaView } from '../features/qualidade-ia/QualidadeIaView';
import {
  useConversationCostByAgent,
  useTopConversationsByCost,
} from '../features/qualidade-ia/use-custo-por-conversa';
import { useQualityAgents } from '../features/supervisor/hooks/use-quality';
import { adaptAgents } from '../features/supervisor/telas/dashboard-qualidade/tela6-adapter';
import { useCapability } from '../hooks/use-capability';
import { api } from '../lib/api-client';

import '../features/qualidade-ia/qualidade-ia.css';

import type React from 'react';

/** Janela [now-30d, now] em datas ISO (yyyy-mm-dd) — snapshot macro do Gestor. */
function macroWindow(): { periodFrom: string; periodTo: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return { periodFrom: from.toISOString().slice(0, 10), periodTo: to.toISOString().slice(0, 10) };
}

export function QualidadeIaPage(): React.JSX.Element {
  const canRead = useCapability('quality-aggregations-read');

  // D-B — gate próprio; o inner (e o fetch) só monta com a cap.
  if (!canRead) {
    return (
      <div className="qualidade-ia-screen qualidade-ia-screen--gate">
        <CapabilityLockedBanner
          capability="quality-aggregations-read"
          tierMin="professional"
          fallbackMessage="A observabilidade de Qualidade IA é restrita a perfis de gestão — fale com o administrador do tenant."
        />
      </div>
    );
  }

  return <QualidadeIaPageInner />;
}

function QualidadeIaPageInner(): React.JSX.Element {
  const { data, isLoading, isError } = useQualityAgents(macroWindow());

  // Comparação entre modelos: capability própria (`ai-usage-read`) — gatear o
  // FETCH, não só o render. Sem a cap, modelStats=null (nota honesta no painel).
  const canCost = useCapability('ai-usage-read');
  const w = macroWindow();
  const byModel = useQuery({
    queryKey: ['gestor', 'qualidade-ia', 'by-model', w.periodFrom, w.periodTo],
    queryFn: () => api.getAiCostByModel({ fromDate: w.periodFrom, toDate: w.periodTo }),
    enabled: canCost,
    staleTime: 60_000,
  });

  // Custo por conversa: mesmo gate `ai-usage-read`. Hook já controla `enabled`.
  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);
  const costWindow: { fromDate: string; toDate: string } = {
    fromDate: w.periodFrom,
    toDate: w.periodTo,
  };
  const topConv = useTopConversationsByCost(costWindow);
  const convCost = useConversationCostByAgent(selectedConvId);

  if (isLoading) {
    return (
      <div className="qualidade-ia-screen">
        <p className="qia-empty">Carregando métricas de IA…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="qualidade-ia-screen">
        <p className="qia-empty" role="alert">
          Falha ao carregar as métricas de IA.
        </p>
      </div>
    );
  }

  const { rows } = adaptAgents(data ?? []);
  const modelStats = canCost ? (byModel.data?.results ?? []) : null;

  // topConversations=null → sem a cap (seção oculta). topConversations=[] → empty-state.
  const topConversations = canCost ? (topConv.data?.results ?? []) : null;

  const selectedConversationCost =
    selectedConvId !== null && convCost.data !== undefined
      ? {
          conversationId: convCost.data.conversationId,
          results: convCost.data.results,
          totalTokens: convCost.data.totalTokens,
        }
      : null;

  return (
    <QualidadeIaView
      agents={rows}
      modelStats={modelStats}
      topConversations={topConversations}
      selectedConversationCost={selectedConversationCost}
      onSelectConversation={setSelectedConvId}
    />
  );
}
